import moment from 'moment';

import type { Moment } from 'moment';

interface PreviousHistoryRangeParams {
  currentBoundary: Moment;
  historyFrom: string;
  baseRangeDays: number;
  rangeIndex: number;
}

interface PreviousHistoryRange {
  from: Moment;
  to: Moment;
  isClampedToHistoryFrom: boolean;
}

// Границы истории считаем в UTC, а не в зоне устройства.
//
// Бэк трактует dateS/dateE как МЕСТНОЕ время активного филиала: берёт зону
// филиала и переводит обе границы в UTC
// (CRM.Business/ClientHistory/LogicClientHistory.cs — GetBranchTimezone +
// ZoneTimeToUtc). Раньше фронт строил границы в зоне устройства, поэтому окно
// уезжало на разницу поясов устройства и филиала: на устройстве в Asia/Bangkok
// (+7) с филиалом в +3 история запрашивалась с 05.08 02:47 при записи в
// 04.08 22:47 — и карточка оказывалась пустой (прод, Crm-000-098, лиды
// 9110/9111/9114 целиком, 8975 — 5 записей из 6).
//
// Зону филиала фронт в карточке лида не получает, поэтому вместо угадывания
// зоны берём UTC и даём запас с обеих сторон: любая реальная зона укладывается
// в диапазон от -11 до +14 часов, так что смещение в 14 часов гарантированно
// накрывает границу в обе стороны. Сам бэк делает то же самое для верхней
// границы — добавляет сутки к dateE с комментарием про разные пояса филиалов
// (CRM.API/Controllers/ActionsHistoryController.cs). Лишние записи по краям
// безвредны: слияние истории идёт по стабильным ключам и дубли отбрасывает.
export const LEAD_HISTORY_TIMEZONE_SLACK_HOURS = 14;

// «Сейчас» в UTC: от него отсчитываются все окна загрузки.
export const getLeadHistoryNow = (): Moment => moment.utc();

export const formatLeadHistoryBoundary = (date: Moment): string =>
  date.format('YYYY-MM-DDTHH:mm:ss');

// Границы запроса с запасом на разницу часовых поясов устройства и филиала.
export const formatLeadHistoryRangeStart = (date: Moment): string =>
  formatLeadHistoryBoundary(
    date.clone().subtract(LEAD_HISTORY_TIMEZONE_SLACK_HOURS, 'hours')
  );

export const formatLeadHistoryRangeEnd = (date: Moment): string =>
  formatLeadHistoryBoundary(
    date.clone().add(LEAD_HISTORY_TIMEZONE_SLACK_HOURS, 'hours')
  );

export const getValidHistoryFromMoment = (
  historyFrom: string | null | undefined
): Moment | null => {
  if (!historyFrom) return null;

  const historyFromMoment = moment.utc(historyFrom);

  return historyFromMoment.isValid() ? historyFromMoment : null;
};

export const getPreviousHistoryRange = ({
  currentBoundary,
  historyFrom,
  baseRangeDays,
  rangeIndex,
}: PreviousHistoryRangeParams): PreviousHistoryRange | null => {
  const historyFromMoment = getValidHistoryFromMoment(historyFrom);

  if (!historyFromMoment) return null;
  if (!currentBoundary.isValid()) return null;
  if (!currentBoundary.isAfter(historyFromMoment)) return null;

  const safeRangeIndex = Math.max(1, rangeIndex);
  const candidateFrom = currentBoundary
    .clone()
    .subtract(baseRangeDays * safeRangeIndex, 'days');
  const isClampedToHistoryFrom =
    candidateFrom.isSameOrBefore(historyFromMoment);
  const from = isClampedToHistoryFrom
    ? historyFromMoment.clone()
    : candidateFrom;
  const to = currentBoundary.clone();

  if (!from.isBefore(to)) return null;

  return {
    from,
    to,
    isClampedToHistoryFrom,
  };
};
